import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { jwtDecode } from 'jwt-decode';

import NotificationList from '../../Components/NotificationItems/NotificationList/NotificationList';
import ConfirmationModal from '../../../../../Components/ConfirmationBox/ConfirmationModal';
import './Notifications.scss';

import apiClient from '../../../../../Config/apiClient';
import { showErrorToast, showSuccessToast } from '../../../../../Context/Toaster/Toaster';
import { useNotification } from '../../Context/NotificationContext/NotificationContext';
import { retrieve } from '../../../../../Services/localStorageService';

const NotificationsPath = process.env.REACT_APP_CC_NOTIFICATIONS_BASE_PATH;

const Notifications = () => {
	const [notifications, setNotifications] = useState([]);
	const [loading, setLoading] = useState(true);
	const { setUnreadCount } = useNotification();
	const [userId, setUserId] = useState(null);

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteId, setDeleteId] = useState(null);

	useEffect(() => {
		const token = retrieve('accessToken');
		if (!token) return;

		try {
			const decoded = jwtDecode(token);
			if (decoded?.id) {
				setUserId(decoded.id);
			} else {
				showErrorToast('User ID not found in token.');
			}
		} catch {
			showErrorToast('Invalid or expired token.');
		}
	}, []);

	const fetchNotifications = useCallback(async (toUserId) => {
		try {
			setLoading(true);
			const res = await apiClient.get(`${NotificationsPath}/list`, {
				params: { to_user: toUserId },
			});

			if (res.data.status === 'SUCCESS') {
				const formatted = (res.data.data || []).map((n) => ({
					id: n.id,
					message: n.description,
					time: moment.utc(n.created_date_time).local().fromNow(),
					isRead: n.is_read,
					to_user: n.to_user,
				}));
				setNotifications(formatted);

				setUnreadCount(formatted.filter((n) => !n.isRead).length);
			} else {
				showErrorToast(res.data?.message || 'Failed to fetch notifications.');
			}
		} catch {
			showErrorToast('Server error while fetching notifications.');
		} finally {
			setLoading(false);
		}
	}, [setUnreadCount]);

	useEffect(() => {
		if (userId) {
			fetchNotifications(userId);
		}
	}, [userId, fetchNotifications]);

	const handleMarkAsRead = async (id) => {
		try {
			await apiClient.put(`${NotificationsPath}/mark-as-read?id=${id}`);

			setNotifications((prev) => {
				const updated = prev.map((n) =>
					n.id === id ? { ...n, isRead: true } : n
				);
				setUnreadCount(updated.filter((n) => !n.isRead).length);
				return updated;
			});
		} catch (err) {
			if (err.response?.status === 403) {
				showErrorToast(err.response?.data?.message);
			} else {
				showErrorToast(err.response?.data?.message);
			}
		}
	};

	const handleDelete = (id) => {
		setDeleteId(id);
		setShowDeleteModal(true);
	};

	const confirmDelete = async () => {
		if (!deleteId) return;
		try {
			await apiClient.delete(`${NotificationsPath}/delete?id=${deleteId}`);

			setNotifications((prev) => prev.filter((n) => n.id !== deleteId));
			setUnreadCount((prevCount) => prevCount - 1);

			showSuccessToast('Notification deleted successfully.');
		} catch (err) {
			if (err.response?.status === 403) {
				showErrorToast(err.response?.data?.message);
			} else {
				showErrorToast(err.response?.data?.message);
			}
		} finally {
			setShowDeleteModal(false);
			setDeleteId(null);
		}
	};

	const notificationsWithActions = notifications.map((n) => ({
		...n,
		onMarkAsRead: () => handleMarkAsRead(n.id),
		onDelete: () => handleDelete(n.id),
	}));

	return (
		<main className="full-wraper">
			<div className="main-wraper">
				<div className="container-fluid">
					<div className="main-inner">
						<NotificationList notifications={notificationsWithActions} loading={loading} />
					</div>
				</div>
			</div>

			<ConfirmationModal
				id="deleteNotificationModal"
				show={showDeleteModal}
				title="Delete Notification"
				message="Are you sure you want to delete this notification?"
				confirmText="Yes"
				cancelText="Cancel"
				onConfirm={confirmDelete}
				onCancel={() => setShowDeleteModal(false)}
			/>
		</main>
	);
};

export default Notifications;